import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Gift, Check, Loader2, Heart, Mail, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL;

const GiftCards = () => {
  const { isAuthenticated, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [values, setValues] = useState([]);
  const [selectedValue, setSelectedValue] = useState(null);
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [myGiftCards, setMyGiftCards] = useState([]);
  const [showMyCards, setShowMyCards] = useState(false);
  
  // Check for success
  const giftCardId = searchParams.get('gift_card_id');
  const sessionId = searchParams.get('session_id');
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const [purchasedCard, setPurchasedCard] = useState(null);

  useEffect(() => {
    fetchValues();
    if (isAuthenticated) {
      fetchMyGiftCards();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (sessionId && giftCardId) {
      verifyPayment();
    }
  }, [sessionId, giftCardId]);

  const fetchValues = async () => {
    try {
      const response = await axios.get(`${API}/api/gift-cards/values`);
      setValues(response.data.values);
      if (response.data.values.length > 0) {
        setSelectedValue(response.data.values[1]); // Default to $50
      }
    } catch (error) {
      console.error('Error fetching values:', error);
    }
  };

  const fetchMyGiftCards = async () => {
    try {
      const response = await axios.get(`${API}/api/gift-cards/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyGiftCards(response.data);
    } catch (error) {
      console.error('Error fetching my gift cards:', error);
    }
  };

  const verifyPayment = async () => {
    try {
      const response = await axios.get(`${API}/api/gift-cards/verify/${sessionId}`);
      if (response.data.status === 'paid') {
        setPurchaseSuccess(true);
        setPurchasedCard(response.data.gift_card);
        fetchMyGiftCards();
      }
    } catch (error) {
      console.error('Error verifying payment:', error);
    }
  };

  const handlePurchase = async () => {
    if (!isAuthenticated) {
      toast.error('Faça login para comprar um vale presente');
      navigate('/auth');
      return;
    }

    if (!selectedValue) {
      toast.error('Selecione um valor');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/api/gift-cards/purchase`,
        {
          value: selectedValue,
          recipient_name: recipientName || null,
          recipient_email: recipientEmail || null,
          message: message || null,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Redirect to Stripe
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar compra');
      setLoading(false);
    }
  };

  if (purchaseSuccess && purchasedCard) {
    return (
      <div className="min-h-screen py-20" data-testid="gift-card-success">
        <div className="container-luxury">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-500" />
            </div>
            
            <h1 className="text-3xl font-bold mb-4">Vale Presente Comprado!</h1>
            <p className="text-muted-foreground mb-8">
              Seu vale presente foi criado com sucesso.
            </p>
            
            <div className="bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/30 p-8 rounded-lg mb-8">
              <Gift className="w-12 h-12 text-primary mx-auto mb-4" />
              <div className="text-4xl font-bold gold-gradient mb-2">
                ${purchasedCard.value.toFixed(2)}
              </div>
              <div className="text-2xl font-mono bg-card px-4 py-2 rounded inline-block">
                {purchasedCard.code}
              </div>
              <p className="text-sm text-muted-foreground mt-4">
                Use este código no checkout para aplicar o desconto
              </p>
            </div>
            
            {purchasedCard.recipient_email && (
              <p className="text-sm text-muted-foreground mb-4">
                Um email será enviado para {purchasedCard.recipient_email}
              </p>
            )}
            
            <div className="flex gap-4 justify-center">
              <Button onClick={() => navigate('/shop')} data-testid="continue-shopping-btn">
                Continuar Comprando
              </Button>
              <Button variant="outline" onClick={() => {
                setPurchaseSuccess(false);
                setPurchasedCard(null);
                navigate('/gift-cards');
              }}>
                Comprar Outro
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12" data-testid="gift-cards-page">
      <div className="container-luxury">
        {/* Header */}
        <div className="text-center mb-12">
          <Gift className="w-16 h-16 text-primary mx-auto mb-4" />
          <h1 className="text-4xl font-bold mb-4">Vale Presente</h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Presenteie alguém especial com um vale presente INDY ELEGANCE. 
            Perfeito para qualquer ocasião!
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-5xl mx-auto">
          {/* Gift Card Selection */}
          <div className="space-y-8">
            <div>
              <h2 className="text-xl font-semibold mb-4">Escolha o Valor</h2>
              <div className="grid grid-cols-3 gap-3">
                {values.map((value) => (
                  <button
                    key={value}
                    onClick={() => setSelectedValue(value)}
                    className={`p-4 border rounded-lg text-center transition-all ${
                      selectedValue === value
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-white/10 hover:border-primary/50'
                    }`}
                    data-testid={`gift-value-${value}`}
                  >
                    <div className="text-2xl font-bold">${value}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <h2 className="text-xl font-semibold">Personalizar (Opcional)</h2>
              
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">
                  <User className="w-4 h-4 inline mr-1" />
                  Nome do Presenteado
                </label>
                <Input
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  placeholder="Nome de quem vai receber"
                  data-testid="recipient-name-input"
                />
              </div>
              
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">
                  <Mail className="w-4 h-4 inline mr-1" />
                  Email do Presenteado
                </label>
                <Input
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  placeholder="email@exemplo.com"
                  data-testid="recipient-email-input"
                />
              </div>
              
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">
                  <Heart className="w-4 h-4 inline mr-1" />
                  Mensagem
                </label>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Escreva uma mensagem especial..."
                  rows={3}
                  data-testid="gift-message-input"
                />
              </div>
            </div>
          </div>

          {/* Preview & Purchase */}
          <div>
            <div className="bg-gradient-to-br from-primary/20 via-background to-primary/10 border border-primary/30 p-8 rounded-lg sticky top-28">
              <div className="text-center mb-6">
                <Gift className="w-16 h-16 text-primary mx-auto mb-4" />
                <h3 className="text-lg text-muted-foreground">INDY ELEGANCE</h3>
                <div className="text-5xl font-bold gold-gradient my-4">
                  ${selectedValue || 0}
                </div>
                <p className="text-sm text-muted-foreground">Vale Presente</p>
              </div>
              
              {recipientName && (
                <div className="text-center mb-4 p-3 bg-card/50 rounded">
                  <p className="text-sm text-muted-foreground">Para:</p>
                  <p className="font-semibold">{recipientName}</p>
                </div>
              )}
              
              {message && (
                <div className="text-center mb-6 p-3 bg-card/50 rounded">
                  <p className="text-sm italic">"{message}"</p>
                </div>
              )}
              
              <Button
                onClick={handlePurchase}
                disabled={loading || !selectedValue}
                className="w-full btn-primary h-14 text-lg"
                data-testid="purchase-gift-card-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Processando...
                  </>
                ) : (
                  <>
                    <Gift className="w-5 h-5 mr-2" />
                    Comprar Vale Presente
                  </>
                )}
              </Button>
              
              <p className="text-xs text-center text-muted-foreground mt-4">
                Pagamento seguro via Stripe
              </p>
            </div>
          </div>
        </div>

        {/* My Gift Cards */}
        {isAuthenticated && myGiftCards.length > 0 && (
          <div className="mt-16 max-w-3xl mx-auto">
            <button
              onClick={() => setShowMyCards(!showMyCards)}
              className="flex items-center gap-2 text-primary hover:underline mb-4"
            >
              <Gift className="w-5 h-5" />
              Meus Vales Presente ({myGiftCards.length})
            </button>
            
            {showMyCards && (
              <div className="grid gap-4">
                {myGiftCards.map((card) => (
                  <div
                    key={card.id}
                    className="bg-card border border-white/10 p-4 rounded-lg flex items-center justify-between"
                  >
                    <div>
                      <div className="font-mono text-lg">{card.code}</div>
                      <div className="text-sm text-muted-foreground">
                        {card.recipient_name ? `Para: ${card.recipient_name}` : 'Uso próprio'}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold">
                        ${card.remaining_value.toFixed(2)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        de ${card.value.toFixed(2)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GiftCards;
